<template>
  <div class="view admin contractor">
    <div class="page" v-if="contractor">
      <a-page-header
        class="border rounded"
        :title="'Contractor Details'"
        :sub-title="`${contractor.name ? contractor.name : 'Unnamed Contractor'}`"
        @back="() => $router.push('/admin/contractors')"
      >
        <template v-slot:tags
          ><a-tag color="blue">{{ String(contractor.status).toUpperCase() }}</a-tag></template
        >

        <template slot="extra">
          <a-button key="1" type="primary" @click="onSave"> {{ $t('crm.details.save') }} </a-button>
        </template>

        <a-descriptions size="small" :column="2" bordered>
          <a-descriptions-item label="Contractor ID" span="2"> {{ contractor._id }} </a-descriptions-item>
          <a-descriptions-item label="Creator" v-if="contractor.created_by">
            {{ contractor.created_by.display_name }}
          </a-descriptions-item>
          <a-descriptions-item label="Updator" v-if="contractor.updated_by">
            {{ contractor.updated_by.display_name }}
          </a-descriptions-item>
          <a-descriptions-item label="Created At">
            <span class="badge badge-primary"> {{ contractor.created_at | moment('lll') }}</span>
          </a-descriptions-item>
          <a-descriptions-item label="Updated At">
            <span class="badge badge-success"> {{ contractor.updated_at | moment('lll') }}</span>
          </a-descriptions-item>
        </a-descriptions>
      </a-page-header>

      <div class="content">
        <a-tabs v-model="current_tab" :defaultActiveKey="1" class="kit-tabs kit-tabs-bold">
          <a-tab-pane :key="1" tab="Basic Info" class="px-2 py-4">
            <a-form-model :model="contractor" :label-col="labelCol" :wrapper-col="wrapperCol" compact>
              <h5 class="text-black mt-2 mb-3"><strong>Contractor Information</strong></h5>

              <a-row type="flex">
                <!-- column left -->
                <a-col :span="24" :md="12">
                  <!-- Active -->
                  <a-form-model-item label="Activated" prop="activated">
                    <a-switch v-model="contractor.activated" :size="ui_size" />
                  </a-form-model-item>

                  <!-- Slug -->
                  <a-form-model-item label="Slug">
                    <a-input v-model="contractor.slug" placeholder="Slug" :size="ui_size" />
                  </a-form-model-item>

                  <!-- Name -->
                  <a-form-model-item label="Short Name">
                    <a-input v-model="contractor.name" placeholder="Name" :size="ui_size" />
                  </a-form-model-item>

                  <!-- Legal Name -->
                  <a-form-model-item label="Legal Name">
                    <a-input v-model="contractor.legal_name" placeholder="Legal Name" :size="ui_size" />
                  </a-form-model-item>

                  <!-- Owner Name -->
                  <a-form-model-item label="Owner Name">
                    <a-input v-model="contractor.owner" placeholder="Owner Name" :size="ui_size" />
                  </a-form-model-item>

                  <!-- Registration Date -->
                  <a-form-model-item label="Registration Date">
                    <a-date-picker v-model="contractor.registration_date" :size="ui_size" />
                  </a-form-model-item>

                  <!-- Business Number -->
                  <a-form-model-item label="Business Number">
                    <a-input v-model="contractor.business_number" placeholder="Business Number" :size="ui_size" />
                  </a-form-model-item>

                  <!-- Contractor Type -->
                  <a-form-model-item label="Type">
                    <a-select v-model="contractor.type" default-value="general" :size="ui_size">
                      <a-select-option
                        v-for="option in options_['CONTRACTOR_TYPES']"
                        :key="option.key"
                        :value="option.key"
                      >
                        {{ option.value }}
                      </a-select-option>
                    </a-select>
                  </a-form-model-item>

                  <!-- Email -->
                  <a-form-model-item label="Email">
                    <a-input v-model="contractor.email" placeholder="Email" :size="ui_size" />
                  </a-form-model-item>

                  <!-- Phone -->
                  <a-form-model-item label="Phone">
                    <a-input-group compact>
                      <a-input v-model="contractor.phone" style="width: 50%" placeholder="Number" :size="ui_size" />
                      <a-input
                        v-model="contractor.phone_extension"
                        style="width: 50%"
                        placeholder="Extension"
                        :size="ui_size"
                      />
                    </a-input-group>
                  </a-form-model-item>

                  <!-- Secondary Phone -->
                  <a-form-model-item label="Secondary Phone">
                    <a-input-group compact>
                      <a-input
                        v-model="contractor.secondary_phone"
                        style="width: 50%"
                        placeholder="Secondary Phone"
                        :size="ui_size"
                      />
                      <a-input
                        v-model="contractor.secondary_phone_extension"
                        style="width: 50%"
                        placeholder="Extension"
                        :size="ui_size"
                      />
                    </a-input-group>
                  </a-form-model-item>

                  <!-- WeChat -->
                  <a-form-model-item label="WeChat">
                    <a-input v-model="contractor.wechat" placeholder="WeChat" :size="ui_size" />
                  </a-form-model-item>

                  <!-- Raw Address -->
                  <a-form-model-item label="Address(manually)">
                    <a-input v-model="contractor.raw_address" :size="ui_size" />
                  </a-form-model-item>

                  <!-- Location -->
                  <a-form-model-item label="Location">
                    <a-input v-model="contractor.location" />
                  </a-form-model-item>

                  <!-- Primary Language -->
                  <a-form-model-item label="Primary Language">
                    <a-input-group>
                      <a-select v-model="contractor.locale" default-value="en-US" style="width: 100%" :size="ui_size">
                        <a-select-option value="en-US"> English (U.S) </a-select-option>
                        <a-select-option value="fr-FR"> French (France) </a-select-option>
                        <a-select-option value="zh-CN"> 中文 (中国大陆) </a-select-option>
                      </a-select>
                    </a-input-group>
                  </a-form-model-item>
                </a-col>
                <!-- COLUMN RIGHT -->
                <a-col :span="24" :md="12">
                  <!-- Status -->
                  <a-form-model-item label="Status" prop="status">
                    <a-select v-model="contractor.status" default-value="active" :size="ui_size">
                      <a-select-option value="pending"> Pending </a-select-option>
                      <a-select-option value="active"> Active </a-select-option>
                      <a-select-option value="inactive"> Inactive </a-select-option>
                      <a-select-option value="lost"> Lost </a-select-option>
                      <a-select-option value="baned"> Baned </a-select-option>
                    </a-select>
                  </a-form-model-item>

                  <!-- Source -->
                  <a-form-model-item label="Source">
                    <a-input-group>
                      <a-select v-model="contractor.source" default-value="unknown" style="width: 100%" :size="ui_size">
                        <a-select-option
                          v-for="option in options_['GENERAL_SOURCES']"
                          :key="option.key"
                          :value="option.key"
                        >
                          {{ option.value }}
                        </a-select-option>
                      </a-select>
                    </a-input-group>
                  </a-form-model-item>

                  <!-- Dscription -->
                  <a-form-model-item label="Description">
                    <a-input
                      v-model="contractor.description"
                      type="textarea"
                      :rows="15"
                      placeholder="Description"
                      :size="ui_size"
                    />
                  </a-form-model-item>

                  <!-- Referer -->
                  <a-form-model-item label="Referer">
                    <a-input v-model="contractor.referer" />
                  </a-form-model-item>

                  <!-- Note -->
                  <a-form-model-item label="Note">
                    <a-input v-model="contractor.note" type="textarea" :rows="4" />
                  </a-form-model-item>
                </a-col>
              </a-row>

              <!-- Actions -->
              <a-form-model-item :wrapper-col="{ span: 14, offset: 4 }">
                <a-button type="primary" @click="onSave"> Update </a-button>
                <a-button style="margin-left: 10px" @click="$router.push('/contractors/contractors')">
                  Cancel
                </a-button>
              </a-form-model-item>
            </a-form-model>
          </a-tab-pane>
          <a-tab-pane :key="2" tab="Projects" class="px-2 py-4">
            <div class="card p-3">
              <!-- TODO -->
            </div>
          </a-tab-pane>
          <a-tab-pane :key="3" tab="History" class="px-2 py-4">
            <div class="card p-3">
              <h1>History</h1>
              <!-- TODO -->
            </div>
          </a-tab-pane>
        </a-tabs>
      </div>
    </div>
  </div>
</template>

<script>
import store from '@/store'

export default {
  components: {},
  data() {
    return {
      ui_size: 'large',
      current_tab: 1,
      contractor: null,
      labelCol: { xs: 24, sm: 5 },
      wrapperCol: { xs: 24, sm: 19 },
    }
  },
  watch: {
    current_tab(val) {
      // if (val === 2 && this.contractor) {
      //   store.dispatch('contractors/fetchProjectsByContractor', this.contractor._id)
      // }
    },
  },
  computed: {
    // projects: () => store.getters['contractors/projects'] || null,
    registration_date: {
      get() {
        return this.$moment(this.contractor.registration_date)
      },
      set(date) {
        this.contractor.registration_date = date
      },
    },
  },
  methods: {
    onTabChange(e) {
      console.log(e)
    },
    async onSave() {
      if (this.contractor) {
        const contractor = await store.dispatch('contractors/updateContractor', this.contractor)
        if (contractor) {
          this.contractor = contractor
          this.$message.success(`Contractor ${contractor.name} has been updated successfully!`)
        } else {
          this.$message.error('Oops, The contractor update failed!')
        }
      }
    },
  },
  async created() {
    if (this.$route.params.id) {
      this.contractor = await store.dispatch('contractors/fetchContractor', this.$route.params.id)
    }
  },
}
</script>

<style lang="scss" scoped>
.ant-tabs-tabpane {
  border-top: 1px solid #dee2e6;
}
</style>
